import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { AppsOutlined, CategoryOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import { v2Colors } from '../design/theme';
import PageState from '../layouts/PageState';
import { useV2Common } from '../state/CommonProvider';
import { getCatalogCategories, getProducts } from '../services/commerce';
import { trackGa4ViewItemList } from '../services/firebaseAnalytics';
import { paths } from '../config/paths';
import { getProductUrlSlug } from '../utils/productUrl';
import { categoryNameToSlug, slugToCategoryName } from '../../routing/categorySlugMap';

// ---------------------------------------------------------------------------
// Category navigation
// ---------------------------------------------------------------------------

const categoryThumbSx = {
  width: 40,
  height: 40,
  borderRadius: 1.5,
  flexShrink: 0,
  objectFit: 'cover',
  bgcolor: v2Colors.tagFill,
};

function CategoryThumb({ image, selected, fallbackIcon: FallbackIcon }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = Boolean(image) && !imgFailed;

  if (showImg) {
    return (
      <Box
        component="img"
        src={image}
        alt=""
        sx={{
          ...categoryThumbSx,
          border: '1px solid',
          borderColor: selected ? v2Colors.primary : v2Colors.borderMuted,
        }}
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <Box
      sx={{
        ...categoryThumbSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid',
        borderColor: selected ? `${v2Colors.primary}55` : v2Colors.borderMuted,
        bgcolor: selected ? `${v2Colors.primary}12` : v2Colors.tagFill,
      }}
    >
      <FallbackIcon sx={{ fontSize: 20, color: selected ? v2Colors.primary : v2Colors.textMuted }} />
    </Box>
  );
}

function CategoryNavItem({ label, image, selected, onClick, fallbackIcon }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: 'calc(100% - 16px)',
        mx: 1,
        my: 0.375,
        px: 1.25,
        py: 1,
        border: 'none',
        borderRadius: 1.5,
        borderLeft: '3px solid',
        borderLeftColor: selected ? v2Colors.primary : 'transparent',
        bgcolor: selected ? v2Colors.selectedFill : 'transparent',
        color: selected ? v2Colors.primary : v2Colors.heading,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: selected ? v2Colors.selectedFill : `${v2Colors.primary}0C`,
        },
        '&:focus-visible': {
          outline: `2px solid ${v2Colors.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      <CategoryThumb image={image} selected={selected} fallbackIcon={fallbackIcon} />
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: selected ? 700 : 500,
          fontSize: '0.875rem',
          lineHeight: 1.35,
          letterSpacing: '-0.01em',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CategoryBar({ categories, selected, onSelect }) {
  const items = [{ key: 'all', name: '', label: 'All' }, ...categories.map((c) => ({ key: c.documentId || c.id, name: c.name, label: c.name }))];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mb: 1.5,
        mt: 1.5,
        mx: { xs: -0.5, sm: 0 },
        px: { xs: 0.5, sm: 0 },
        overflowX: 'auto',
        flexWrap: 'nowrap',
        pb: 0.5,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {items.map(({ key, name, label }) => {
        const active = selected === name;
        return (
          <Chip
            key={key}
            label={label}
            clickable
            onClick={() => onSelect(name)}
            sx={{
              flexShrink: 0,
              fontWeight: active ? 700 : 500,
              fontSize: '0.8125rem',
              height: 36,
              borderRadius: 2,
              border: '1px solid',
              borderColor: active ? v2Colors.primary : v2Colors.borderMuted,
              bgcolor: active ? v2Colors.primary : v2Colors.white,
              color: active ? v2Colors.white : v2Colors.heading,
              transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
              '&:hover': {
                bgcolor: active ? v2Colors.primaryDark : `${v2Colors.primary}0C`,
                borderColor: v2Colors.primary,
              },
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        );
      })}
    </Box>
  );
}

function CategorySidebar({ categories, selected, onSelect }) {
  return (
    <Box
      component="nav"
      aria-label="Product categories"
      sx={{
        position: 'sticky',
        top: { md: 72 },
        borderRadius: 2.5,
        border: `1px solid ${v2Colors.border}`,
        bgcolor: v2Colors.white,
        boxShadow: '0 1px 3px rgba(26, 19, 15, 0.06)',
        overflow: 'hidden',
        maxHeight: { md: 'calc(100vh - 88px)' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: `1px solid ${v2Colors.borderMuted}`,
          bgcolor: v2Colors.tagFill,
        }}
      >
        <CategoryOutlined sx={{ fontSize: 20, color: v2Colors.primary }} />
        <Box>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: v2Colors.primaryMuted,
              fontSize: '0.65rem',
            }}
          >
            Browse
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: v2Colors.heading, lineHeight: 1.2 }}
          >
            Categories
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          overflow: 'auto',
          py: 1,
          flex: 1,
          scrollbarWidth: 'thin',
          scrollbarColor: `${v2Colors.borderMuted} transparent`,
        }}
      >
        <CategoryNavItem
          label="All products"
          selected={!selected}
          onClick={() => onSelect('')}
          fallbackIcon={AppsOutlined}
        />
        {categories.map((cat) => (
          <CategoryNavItem
            key={cat.documentId || cat.id}
            label={cat.name}
            image={cat.image}
            selected={selected === cat.name}
            onClick={() => onSelect(cat.name)}
            fallbackIcon={CategoryOutlined}
          />
        ))}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Product card
// ---------------------------------------------------------------------------

function ProductCard({ product, onClick }) {
  const lowestVariant = product.variants
    ?.filter((v) => v.visibleToCustomer !== false)
    .sort((a, b) => a.price - b.price)[0];

  const outOfStock = lowestVariant && lowestVariant.isInStock === false;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        opacity: outOfStock ? 0.55 : 1,
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': {
          borderColor: { md: 'primary.light' },
          boxShadow: { md: 3 },
        },
        '&:active': { bgcolor: 'action.hover' },
      }}
    >
      <CardMedia
        component="img"
        image={product.image || product.images?.[0] || noImage}
        alt={product.name}
        sx={{
          width: '100%',
          height: { md: 'auto' },
          aspectRatio: { md: '4 / 3' },
          maxHeight: { md: 220 },
          objectFit: 'cover',
          bgcolor: 'grey.100',
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = noImage;
          e.target.style.objectFit = 'contain';
          e.target.style.padding = '16px';
        }}
      />

      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}>
            {product.name}
          </Typography>
          {lowestVariant ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.25 }}>
              <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1 }}>
                ₹{lowestVariant.price}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lowestVariant.weight}
              </Typography>
            </Box>
          ) : null}
          {outOfStock && <Chip label="Out of stock" size="small" sx={{ mt: 0.5, fontSize: 10 }} />}
        </Box>

      </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Product grid skeleton
// ---------------------------------------------------------------------------

function ProductSkeleton() {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Skeleton variant="rectangular" width="100%" sx={{ aspectRatio: '4/3' }} />
            <Box sx={{ p: 1.5 }}>
              <Skeleton width="70%" height={24} />
              <Skeleton width="30%" height={28} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Catalog page
// ---------------------------------------------------------------------------

export default function Catalog() {
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams();
  const { isDesktop } = useV2Common();
  const selectedCategory = routeSlug === 'all' || !routeSlug ? '' : (slugToCategoryName(routeSlug) ?? '');

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const lastTrackedListCategory = useRef(null);

  // Load categories once
  useEffect(() => {
    getCatalogCategories()
      .then((res) => setCategories(res.data ?? []))
      .catch(() => {});
  }, []);

  const loadProducts = useCallback(
    async (category, page = 1, append = false) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const res = await getProducts({ category: category || undefined, page });
        if (append) {
          setProducts((prev) => [...prev, ...(res.data ?? [])]);
        } else {
          setProducts(res.data ?? []);
        }
        setPagination(res.pagination ?? null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Reload products when category changes
  useEffect(() => {
    loadProducts(selectedCategory, 1, false);
  }, [selectedCategory, loadProducts]);

  useEffect(() => {
    if (loading || error || products.length === 0) return;
    if (lastTrackedListCategory.current === selectedCategory) return;
    lastTrackedListCategory.current = selectedCategory;
    trackGa4ViewItemList({
      listId: selectedCategory || 'all',
      listName: selectedCategory || 'All products',
      products,
    });
  }, [loading, error, selectedCategory, products]);

  const handleCategorySelect = (catName) => {
    if (!catName) {
      navigate(paths.allProducts);
      return;
    }
    navigate(paths.category(categoryNameToSlug(catName)));
  };

  const handleLoadMore = () => {
    if (!pagination) return;
    loadProducts(selectedCategory, pagination.page + 1, true);
  };

  const hasMore = pagination && pagination.page < pagination.pages;

  const productGrid = (
    <>
      {loading && <ProductSkeleton />}

      {!loading && error && (
        <PageState loading={false} error={error} onRetry={() => loadProducts(selectedCategory, 1, false)}>
          {null}
        </PageState>
      )}

      {!loading && !error && products.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography color="text.secondary">No products found</Typography>
        </Box>
      )}

      {!loading && !error && products.length > 0 && (
        <>
          <Grid container spacing={2.5}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <ProductCard
                  product={product}
                  onClick={() => {
                    const productSlug = getProductUrlSlug(product);
                    if (productSlug) navigate(paths.product(productSlug));
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              {loadingMore ? (
                <CircularProgress size={24} />
              ) : (
                <Chip label="Load more" variant="outlined" clickable onClick={handleLoadMore} />
              )}
            </Box>
          )}
        </>
      )}
    </>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 0 }, pt: { xs: 1, md: 2 }, pb: { xs: 10, md: 3 } }}>
      {isDesktop && categories.length > 0 ? (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item md={3}>
            <CategorySidebar
              categories={categories}
              selected={selectedCategory}
              onSelect={handleCategorySelect}
            />
          </Grid>
          <Grid item md={9}>
            {isDesktop && selectedCategory ? (
              <Typography variant="h6" fontWeight={800} sx={{ mb: 2, letterSpacing: '-0.02em' }}>
                {selectedCategory}
              </Typography>
            ) : isDesktop ? (
              <Typography variant="h6" fontWeight={800} sx={{ mb: 2, letterSpacing: '-0.02em' }}>
                All products
              </Typography>
            ) : null}
            {productGrid}
          </Grid>
        </Grid>
      ) : (
        <>
          {categories.length > 0 && (
            <CategoryBar
              categories={categories}
              selected={selectedCategory}
              onSelect={handleCategorySelect}
            />
          )}
          {productGrid}
        </>
      )}
    </Box>
  );
}
